import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, CircleCheckBig, CircleX, Database, LoaderCircle, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { DbtProjectSection } from '../components/DbtProjectSection';
import { SnowflakeConfigForm, type SnowflakeConnectionConfig } from '../components/SnowflakeConfigForm';

interface TestResult {
  rudderDestinationId: string;
  success: boolean;
  database?: string;
  tableCount?: number;
  error?: string;
}

export function SettingsWarehouse(): React.ReactElement {
  usePageTitle('Warehouse — Settings');
  const queryClient = useQueryClient();
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();
  const [connectionStrings, setConnectionStrings] = useState<Record<string, string>>({});
  const [snowflakeConfigs, setSnowflakeConfigs] = useState<Record<string, SnowflakeConnectionConfig>>({});
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery(orpc.settings.warehouses.list.queryOptions({ input: {} }));

  const warehouses = data?.warehouses ?? [];
  const connections = useMemo(() => data?.connections ?? [], [data?.connections]);

  const getConnection = (id: string) => connections.find((c) => c.rudderDestinationId === id);

  const isConfigured = (id: string) => {
    const conn = getConnection(id);
    return !!(conn?.connectionString ?? conn?.config);
  };

  // Initialize connection strings and snowflake configs from redacted values (render-phase adjustment)
  const [prevConnections, setPrevConnections] = useState(connections);
  if (connections !== prevConnections) {
    setPrevConnections(connections);
    if (connections.length > 0) {
      const initial: Record<string, string> = {};
      const initialSnowflake: Record<string, SnowflakeConnectionConfig> = {};
      for (const c of connections) {
        if (c.connectionString) {
          initial[c.rudderDestinationId] = c.connectionString;
        }
        if (c.config) {
          initialSnowflake[c.rudderDestinationId] = c.config;
        }
      }
      setConnectionStrings((prev) => {
        const merged = { ...initial };
        for (const [k, v] of Object.entries(prev)) {
          if (v && !v.includes('••••')) merged[k] = v;
        }
        return merged;
      });
      setSnowflakeConfigs((prev) => ({ ...initialSnowflake, ...prev }));
    }
  }

  const syncMutation = useMutation(
    orpc.settings.warehouses.sync.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.settings.warehouses.list.queryOptions({ input: {} }).queryKey,
        });
        toast.success('Warehouses synced from RudderStack');
      },
      onError: () => {
        toast.error('Failed to sync warehouses');
      },
    }),
  );

  const saveMutation = useMutation(
    orpc.settings.warehouses.updateConnection.mutationOptions({
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: orpc.settings.warehouses.list.queryOptions({ input: {} }).queryKey,
        });
        // Collapse after save (or remove)
        if (variables.connectionString === null && !variables.connectionConfig) {
          toast.success('Connection removed');
        } else {
          toast.success('Connection saved');
        }
        setExpandedId(null);
      },
      onError: () => {
        toast.error('Failed to save connection');
      },
    }),
  );

  const testMutation = useMutation(
    orpc.settings.warehouses.testConnection.mutationOptions({
      onSuccess: (result, variables) => {
        setTestResults((prev) => ({
          ...prev,
          [variables.rudderDestinationId]: {
            rudderDestinationId: variables.rudderDestinationId,
            ...result,
          },
        }));
      },
      onError: (_err, variables) => {
        setTestResults((prev) => ({
          ...prev,
          [variables.rudderDestinationId]: {
            rudderDestinationId: variables.rudderDestinationId,
            success: false,
            error: 'Network error',
          },
        }));
      },
    }),
  );

  const handleSave = useCallback(
    (id: string) => saveMutation.mutate({ rudderDestinationId: id, connectionString: connectionStrings[id] ?? null }),
    [saveMutation, connectionStrings],
  );

  const handleRemove = useCallback(
    (id: string) => {
      setConnectionStrings((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      setTestResults((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      saveMutation.mutate({ rudderDestinationId: id, connectionString: null });
    },
    [saveMutation],
  );

  const handleTest = useCallback(
    (id: string) => {
      const cs = connectionStrings[id];
      testMutation.mutate({
        rudderDestinationId: id,
        connectionString: cs?.includes('••••') ? undefined : (cs ?? undefined),
      });
    },
    [testMutation, connectionStrings],
  );

  const handleSnowflakeSave = useCallback(
    (id: string, config: SnowflakeConnectionConfig) =>
      saveMutation.mutate({ rudderDestinationId: id, connectionString: null, connectionConfig: config }),
    [saveMutation],
  );

  const handleSnowflakeTest = useCallback(
    (id: string, config: SnowflakeConnectionConfig) => {
      // For managed_key, always send config — backend injects the private key
      if (config.authType === 'managed_key') {
        testMutation.mutate({
          rudderDestinationId: id,
          connectionString: JSON.stringify(config),
          warehouseType: 'SNOWFLAKE',
        });
        return;
      }
      // If no fresh secret was entered (all secrets stripped as redacted), fall back to stored credentials
      const hasSecret = config.password ?? config.privateKey;
      if (hasSecret) {
        testMutation.mutate({
          rudderDestinationId: id,
          connectionString: JSON.stringify(config),
          warehouseType: 'SNOWFLAKE',
        });
      } else {
        testMutation.mutate({ rudderDestinationId: id, warehouseType: 'SNOWFLAKE' });
      }
    },
    [testMutation],
  );

  const handleSnowflakeRemove = useCallback(
    (id: string) => {
      setSnowflakeConfigs((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      setTestResults((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
      saveMutation.mutate({ rudderDestinationId: id, connectionString: null });
    },
    [saveMutation],
  );

  /** Derive a Snowflake pre-fill config from the RudderStack destination config blob. */
  const deriveSnowflakePrefill = useCallback(
    (whId: string): SnowflakeConnectionConfig | undefined => {
      const wh = warehouses.find((w) => w.rudderDestinationId === whId);
      const rsConfig = wh?.config;
      if (!rsConfig) return undefined;
      return {
        account: rsConfig.account ?? '',
        database: rsConfig.database ?? '',
        warehouse: rsConfig.warehouse ?? '',
        schema: rsConfig.namespace ?? '',
        role: rsConfig.role ?? '',
        username: rsConfig.user ?? '',
        authType: 'managed_key',
      };
    },
    [warehouses],
  );

  const isSupported = (type: string) => type === 'POSTGRES' || type === 'RS' || type === 'SNOWFLAKE';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  // Sort: configured first, then by name
  const sorted = [...warehouses].sort((a, b) => {
    const aConf = isConfigured(a.rudderDestinationId) ? 0 : 1;
    const bConf = isConfigured(b.rudderDestinationId) ? 0 : 1;
    if (aConf !== bConf) return aConf - bConf;
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      {!isAdmin && (
        <div className="mb-4 rounded border border-amber-200/50 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Only workspace admins can modify settings.
        </div>
      )}
      {/* Header with sync button */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Warehouses</h2>
        <button
          onClick={() => syncMutation.mutate({})}
          disabled={!isAdmin || syncMutation.isPending}
          className="inline-flex items-center gap-2 rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-30"
        >
          {syncMutation.isPending ? <LoaderCircle size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Sync from RudderStack
        </button>
      </div>

      {warehouses.length === 0 ? (
        <section className="rounded border border-border bg-card p-8 text-center">
          <Database size={32} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="mb-1 text-sm text-muted-foreground">No warehouses found</p>
          <p className="text-xs text-muted-foreground/60">
            Configure your RudderStack credentials in Settings, then sync to discover warehouse destinations.
          </p>
        </section>
      ) : (
        <div className="space-y-2">
          {sorted.map((wh) => {
            const configured = isConfigured(wh.rudderDestinationId);
            const expanded = expandedId === wh.rudderDestinationId;
            const supported = isSupported(wh.type);
            const connStr = connectionStrings[wh.rudderDestinationId] ?? '';
            const testResult = testResults[wh.rudderDestinationId];

            return (
              <section key={wh.rudderDestinationId} className="rounded border border-border bg-card">
                {/* Collapsed row — always visible */}
                <div className="flex items-center gap-3 px-4 py-3">
                  {/* Status dot */}
                  <div
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      configured ? 'bg-green-500' : 'bg-muted-foreground/30'
                    }`}
                  />

                  {/* Name */}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{wh.name}</span>

                  {/* Type badge */}
                  <span className="shrink-0 rounded border border-border bg-background px-2 py-0.5 text-[0.625rem] font-semibold tracking-wider text-muted-foreground uppercase">
                    {wh.type}
                  </span>

                  {/* Status label */}
                  <span className={`shrink-0 text-xs ${configured ? 'text-green-700' : 'text-muted-foreground/50'}`}>
                    {configured ? 'Connected' : supported ? 'Not connected' : 'Coming soon'}
                  </span>

                  {/* Action button */}
                  {supported && (
                    <button
                      onClick={() => setExpandedId(expanded ? null : wh.rudderDestinationId)}
                      disabled={!isAdmin}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-30"
                    >
                      {configured ? (
                        <>
                          <Pencil size={11} />
                          Edit
                        </>
                      ) : (
                        <>
                          <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
                          Configure
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Expanded form — only when open */}
                {expanded && supported && (
                  <div className="border-t border-border px-4 pt-3 pb-4">
                    {wh.type === 'SNOWFLAKE' ? (
                      <SnowflakeConfigForm
                        rudderDestinationId={wh.rudderDestinationId}
                        initialConfig={
                          snowflakeConfigs[wh.rudderDestinationId] ??
                          getConnection(wh.rudderDestinationId)?.config ??
                          deriveSnowflakePrefill(wh.rudderDestinationId)
                        }
                        isAdmin={isAdmin}
                        onSave={handleSnowflakeSave}
                        onTest={handleSnowflakeTest}
                        onRemove={handleSnowflakeRemove}
                        isConfigured={configured}
                        isSaving={saveMutation.isPending}
                        isTesting={testMutation.isPending}
                        testResult={testResults[wh.rudderDestinationId]}
                      />
                    ) : (
                      <>
                        <div className="mb-3">
                          <label className="mb-1 block text-xs text-muted-foreground">Connection String</label>
                          <input
                            type="password"
                            value={connStr}
                            onChange={(e) =>
                              setConnectionStrings((prev) => ({
                                ...prev,
                                [wh.rudderDestinationId]: e.target.value,
                              }))
                            }
                            placeholder="postgresql://user:password@host:5432/db"
                            className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-brand"
                          />
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleTest(wh.rudderDestinationId)}
                            disabled={!isAdmin || testMutation.isPending}
                            className="inline-flex items-center gap-2 rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-30"
                          >
                            {testMutation.isPending && <LoaderCircle size={12} className="animate-spin" />}
                            Test
                          </button>
                          <button
                            onClick={() => handleSave(wh.rudderDestinationId)}
                            disabled={!isAdmin || saveMutation.isPending || !connStr || connStr.includes('••••')}
                            className="inline-flex items-center gap-2 rounded border border-brand bg-brand/10 px-3 py-1.5 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
                          >
                            {saveMutation.isPending && <LoaderCircle size={12} className="animate-spin" />}
                            Save
                          </button>
                          {configured && (
                            <button
                              onClick={() => handleRemove(wh.rudderDestinationId)}
                              disabled={!isAdmin || saveMutation.isPending}
                              className="ml-auto inline-flex items-center gap-1.5 rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-700 disabled:opacity-30"
                            >
                              <Trash2 size={11} />
                              Remove
                            </button>
                          )}
                        </div>

                        {testResult && (
                          <div
                            className={`mt-3 flex items-center gap-2 text-xs ${
                              testResult.success ? 'text-green-700' : 'text-red-700'
                            }`}
                          >
                            {testResult.success ? (
                              <>
                                <CircleCheckBig size={14} />
                                Connected — {testResult.database} ({testResult.tableCount} tables)
                              </>
                            ) : (
                              <>
                                <CircleX size={14} />
                                {testResult.error}
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* dbt Project sub-section — show when connected OR has existing mapping */}
                    <DbtProjectSection warehouseId={wh.rudderDestinationId} isAdmin={isAdmin} />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
